import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { LOCAL_STORAGE_KEY } from "../constants";
import { getMe } from "../services/api/commonApi";
import { User } from "../types/user";

interface UserContextType {
  userAuth: User | null;
  isLoading: boolean;
  error: Error | null;
  refetchUser: () => void;
  clearUser: () => void;
  setUserData: (userData: User) => void;
  initializeUser: () => void;
  shouldLoadUser: () => boolean;
  userPermission: any;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userAuth, setUserAuth] = useState<User | null>(null);
  const [userPermission, setUserPermission] = useState<User | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const shouldLoadUser = () => {
    if (userAuth) return false;
    if (hasAttemptedLoad) return false;

    const token = localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
    return !!token;
  };

  const {
    data: userData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["getMe"],
    queryFn: getMe,
    enabled: shouldLoadUser(),
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity
  });

  useEffect(() => {
    if (userData) {
      setUserAuth(userData);
      setUserPermission(userData?.permissions);
      setHasAttemptedLoad(true);
    }
  }, [userData]);

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
    if (token && !userAuth && !hasAttemptedLoad) {
      refetch();
      setHasAttemptedLoad(true);
    }
  }, []);

  const refetchUser = async () => {
    try {
      const result = await refetch();
      if (result.data) {
        setUserAuth(result.data);
        setUserPermission(result?.data?.permissions);
        setHasAttemptedLoad(true);
      }
    } catch (error) {
      console.error("Failed to refetch user:", error);
      setHasAttemptedLoad(true);
    }
  };

  const clearUser = () => {
    setUserAuth(null);

    setHasAttemptedLoad(false);
  };

  const setUserData = (userData: User) => {
    setUserAuth(userData);
    setUserPermission(userData?.permissions);
    setHasAttemptedLoad(true);
  };

  const initializeUser = async () => {
    try {
      const result = await refetch();
      if (result.data) {
        setUserAuth(result.data);
        setUserPermission(result?.data?.permissions);
        setHasAttemptedLoad(true);
      }
    } catch (error) {
      console.error("Failed to initialize user:", error);
      setHasAttemptedLoad(true);
    }
  };

  const value: UserContextType = {
    userAuth,
    isLoading,
    error,
    refetchUser,
    clearUser,
    setUserData,
    initializeUser,
    shouldLoadUser,
    userPermission
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
